import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from './ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import helpers from 'dom-helpers';
import Loading from './Loading';

const sides = {
    top: 0,
    right: 1,
    bottom: 2,
    left: 3
};

const storeSize = {};

const PreviewOriginal = ({ 
    url, 
    placement = 'top', 
    originalWidth = 0, 
    originalHeight = 0, 
    maxWidth, 
    maxHeight, 
    indents = [7,7,7,7], 
    overlayID, 
    updatePosition, 
    t 
}) => {
    const imgRef = useRef(null);
    
    const getStoreSize = useCallback((urlParam = url) => {
        return storeSize[urlParam] || {};
    }, [url]);

    const [state, setState] = useState(() => {
        const size = getStoreSize(url);
        return {
            url: url,
            overlay: overlayID && document.getElementById(overlayID) || null,
            originalWidth: originalWidth || size.width,
            originalHeight: originalHeight || size.height,
            maxWidth: document.documentElement.clientWidth,
            maxHeight: document.documentElement.clientHeight,
            clientWidth: document.documentElement.clientWidth,
            clientHeight: document.documentElement.clientHeight
        };
    });

    const getIndent = useCallback((side) => {
        return indents[sides[side]] || 0;
    }, [indents]);

    const calcSize = useCallback((isHorizontal, ratio, maxWidth, maxHeight) => {
        let width = 0;
        let height = 0;

        if (isHorizontal) {
            width = maxWidth;
            height = width / ratio;
        } else {
            height = maxHeight;
            width = height * ratio;
        }

        return {
            width,
            height
        };
    }, []);

    const getMaxSize = useCallback((originalWidth = state.originalWidth, originalHeight = state.originalHeight) => {
        const overlayBox = helpers.offset(state.overlay);
        const scrollTop = document.documentElement.scrollTop;
        let maxWidth = state.maxWidth - getIndent('left') - getIndent('right');
        let maxHeight = state.maxHeight - getIndent('top') - getIndent('bottom');

        if (placement === 'top') {
            maxHeight -= state.clientHeight - (overlayBox.top - scrollTop);
        } else if (placement === 'bottom') {
            maxHeight -= overlayBox.top - scrollTop + overlayBox.height;
        } else if (placement === 'left') {
            maxWidth -= state.clientWidth - overlayBox.left;
        } else if (placement === 'right') {
            maxWidth -= overlayBox.left + overlayBox.width;
        }

        return {
            maxWidth: maxWidth > originalWidth ? originalWidth : maxWidth,
            maxHeight: maxHeight > originalHeight ? originalHeight : maxHeight
        };
    }, [state, placement, getIndent]);

    const getSize = useCallback((originalWidth = state.originalWidth, originalHeight = state.originalHeight) => {
        const ratio = originalWidth / originalHeight;
        const maxSize = getMaxSize(originalWidth, originalHeight);
        const isHorizontal = ratio > 1;
        let size = calcSize(isHorizontal, ratio, maxSize.maxWidth, maxSize.maxHeight);

        if (isHorizontal && size.height > maxSize.maxHeight) {
            size = calcSize(false, ratio, maxSize.maxWidth, maxSize.maxHeight);
        } else if (!isHorizontal && size.width > maxSize.maxWidth) {
            size = calcSize(true, ratio, maxSize.maxWidth, maxSize.maxHeight);
        }

        return {
            width: size.width,
            height: size.height
        };
    }, [state.originalWidth, state.originalHeight, getMaxSize, calcSize]);

    const setPositionTooltip = useCallback((size) => {
        const tooltip = helpers.querySelectorAll(document, '.tooltip[role="tooltip"]')[0];
        const scrollTop = document.documentElement.scrollTop;
        let position = helpers.position(tooltip);

        if (scrollTop + state.clientHeight < position.top + size.height) {
            helpers.style(tooltip, {
                top: (scrollTop + state.clientHeight - size.height - getIndent('top') - getIndent('bottom')) + 'px'
            });
        }

        if (state.clientWidth < position.left + size.width) {
            helpers.style(tooltip, {
                left: (state.clientWidth - size.width - getIndent('left') - getIndent('right')) + 'px'
            });
        }
    }, [state.clientHeight, state.clientWidth, getIndent]);

    const onLoad = useCallback(() => {
        let size = getSize(imgRef.current.width, imgRef.current.height);

        storeSize[state.url] = {
            width: imgRef.current.width,
            height: imgRef.current.height
        };

        setPositionTooltip(size);
        setState(prevState => ({
            ...prevState,
            originalWidth: imgRef.current.width,
            originalHeight: imgRef.current.height,
            ...size
        }));
        
        updatePosition && updatePosition();
    }, [getSize, setPositionTooltip, state.url, updatePosition]);

    const initImage = useCallback(() => {
        imgRef.current = new Image();
        imgRef.current.src = state.url;
        imgRef.current.onload = onLoad;
    }, [state.url, onLoad]);

    useEffect(() => {
        if (state.originalWidth && state.originalHeight) {
            setState(prevState => ({...prevState, ...getSize()}));
        } else {
            initImage();
        }

        return () => {
            if (imgRef.current) {
                imgRef.current.onload = () => {};
            }
        };
    }, [state.originalWidth, state.originalHeight, getSize, initImage]);

    return (
        <div className={classNames({
            'preview-original': true,
            'loading': !state.originalWidth
        })}>
            {!state.originalWidth
                ? <Loading absolute={true} center={true} />
                : (
                    <img
                        className="preview-original__image"
                        src={state.url}
                        width={state.width}
                        height={state.height}
                    />
                )
            }
        </div>
    );
};

PreviewOriginal.propTypes = {
    url: PropTypes.string,
    placement: PropTypes.string,
    originalWidth: PropTypes.number,
    originalHeight: PropTypes.number,
    maxWidth: PropTypes.number,
    maxHeight: PropTypes.number,
    indents: PropTypes.array,
    t: PropTypes.object
};

export default withTranslation(PreviewOriginal);